/**
 * Canonical frontend hostname contract.
 *
 * A single Azure instance serves every frontend domain from one bundle, so the
 * environment is decided at runtime by hostname. These helpers are the one place
 * that mapping lives. Both getBackendUrl() (which backend to call) and
 * config/environment.js (Stripe key + analytics env) consume them so the two
 * stay in lockstep.
 */
const PRODUCTION_HOSTS = ['rxrooster.com', 'www.rxrooster.com'];
const STAGING_HOSTS = [
  'test.rxrooster.com',
  'www.test.rxrooster.com',
  'rxrooster-iac-frontend-staging.azurewebsites.net',
];

// The authenticated platform app. Its backend is the stable api.rxrooster.com
// alias (CNAME-routed to whichever backend is live), NOT a bare *.azurewebsites
// hostname — so the app follows an infra cutover via DNS and never has to be
// rebuilt to chase a backend host. Hardcoded here so it can't drift to a bare
// host via a stale REACT_APP_RXROOSTER_BACKEND_URL build value.
const PRODUCTION_APP_HOSTS = ['app.rxrooster.com'];
const PRODUCTION_APP_BACKEND_URL = 'https://api.rxrooster.com';

const normalizeHost = (hostname) =>
  typeof hostname === 'string' ? hostname.toLowerCase() : '';

/**
 * @param {string} hostname
 * @returns {boolean} true for the production landing host (rxrooster.com)
 */
export const isProductionHost = (hostname) =>
  PRODUCTION_HOSTS.includes(normalizeHost(hostname));

/**
 * @param {string} hostname
 * @returns {boolean} true for the staging frontend host (test.rxrooster.com)
 */
export const isStagingHost = (hostname) =>
  STAGING_HOSTS.includes(normalizeHost(hostname));

/**
 * Get the backend API URL
 * Supports runtime override from mobile WebView
 * @returns {string} Backend API URL
 */
export const getBackendUrl = () => {
  // Check for runtime override from mobile WebView first
  if (typeof window !== 'undefined' && window.REACT_APP_RXROOSTER_BACKEND_URL) {
    return window.REACT_APP_RXROOSTER_BACKEND_URL;
  }

  if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
    const host = normalizeHost(window.location.hostname);

    if (host) {
      // Platform app -> the stable api.rxrooster.com alias (infra-cutover-safe).
      if (PRODUCTION_APP_HOSTS.includes(host)) {
        return PRODUCTION_APP_BACKEND_URL;
      }

      const prodBackend = process.env.REACT_APP_RXROOSTER_BACKEND_URL_PROD;
      const stagingBackend = process.env.REACT_APP_RXROOSTER_BACKEND_URL_STAGING;

      if (isProductionHost(host) && prodBackend) {
        return prodBackend;
      }

      if (isStagingHost(host) && stagingBackend) {
        return stagingBackend;
      }
    }
  }

  // Fall back to build-time environment variable
  return process.env.REACT_APP_RXROOSTER_BACKEND_URL || 'http://localhost:3001';
};

export default getBackendUrl;
